import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const publishShipmentAPI = createAsyncThunk(
  'shipment/publish',
  async (shipmentData, { rejectWithValue }) => {
    try {
      const response = await callAPI(apiUrls.shipmentsCreate, {}, 'POST', shipmentData);
      if (response?.data?.success) {
        return response?.data?.data;
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to create shipment');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create shipment');
    }
  }
);

export const submitShipmentAPI = createAsyncThunk(
  'shipment/submit',
  async ({ shipmentId }, { rejectWithValue }) => {
    try {
      const url = `${apiUrls.shipmentsSubmit}/${shipmentId}/submit`;
      const response = await callAPI(url, {}, 'PATCH', {});
      if (response?.data?.success) {
        return {
          success: true,
          message: response?.data?.message || 'Shipment submitted successfully',
          data: response?.data?.data
        };
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to submit shipment');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to submit shipment');
    }
  }
);

export const GetCargoTypes = createAsyncThunk(
  'shipment/cargo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await callAPI(apiUrls.shipmentsCargoTypes, {}, 'GET', {});
      if (response?.data?.success) {
        return response?.data?.data;
      } else {
        return rejectWithValue(response?.data?.message || 'Failed to fetch cargo types');
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to fetch cargo types');
    }
  }
);

const initialState = {
  mutationLoading: false,
  mutationError: null,
  mutationSuccess: null,
  submitSuccess: false,
  submitMessage: null,
  cargoTypesLoading: false,
  cargoTypes: [],

  currentStep: 0,
  shipmentData: {
    transportMode: "road",
    bookingType: "marketplace",
    pickup: {
      country: "",
      state: "",
      city: "",
      date: "",
      address: "",
      countryCode: "",
      location: {
        type: "Point",
        coordinates: [0, 0]
      }
    },
    delivery: {
      country: "",
      state: "",
      city: "",
      date: "",
      address: "",
      countryCode: "",
      location: {
        type: "Point",
        coordinates: [0, 0]
      }
    },
    cargoType: "",
    loadType: "ftl",
    weightKg: "",
    cargoDescription: "",
    dimension: {
      length: "",
      width: "",
      height: "",
      quantity: 1
    },
    specialHandling: "",
    marketplaceType: "public",
    status: "pending_approval",
    createdBy: "",
    is_deleted: false
  },
};

const shipmentSlice = createSlice({
  name: 'shipment',
  initialState,
  reducers: {
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    nextStep: (state) => {
      if (state.currentStep < 3) {
        state.currentStep++;
      }
    },
    prevStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep--;
      }
    },
    updateShipmentData: (state, action) => {
      state.shipmentData = {
        ...state.shipmentData,
        ...action.payload
      };
    },
    updatePickupData: (state, action) => {
      if (action.payload.country !== undefined && action.payload.country !== state.shipmentData.pickup.country) {
        state.shipmentData.pickup = {
          ...state.shipmentData.pickup,
          ...action.payload,
          state: '',
          city: ''
        };
      } else if (action.payload.state !== undefined && action.payload.state !== state.shipmentData.pickup.state) {
        state.shipmentData.pickup = {
          ...state.shipmentData.pickup,
          ...action.payload,
          city: ''
        };
      } else {
        state.shipmentData.pickup = {
          ...state.shipmentData.pickup,
          ...action.payload
        };
      }
      if (action.payload.country) {
        state.shipmentData.pickup.countryCode = action.payload.country;
      }
    },
    updateDeliveryData: (state, action) => {
      if (action.payload.country !== undefined && action.payload.country !== state.shipmentData.delivery.country) {
        state.shipmentData.delivery = {
          ...state.shipmentData.delivery,
          ...action.payload,
          state: '',
          city: ''
        };
      } else if (action.payload.state !== undefined && action.payload.state !== state.shipmentData.delivery.state) {
        state.shipmentData.delivery = {
          ...state.shipmentData.delivery,
          ...action.payload,
          city: ''
        };
      } else {
        state.shipmentData.delivery = {
          ...state.shipmentData.delivery,
          ...action.payload
        };
      }
      if (action.payload.country) {
        state.shipmentData.delivery.countryCode = action.payload.country;
      }
    },
    updateCargoData: (state, action) => {
      state.shipmentData = {
        ...state.shipmentData,
        ...action.payload
      };
    },
    resetShipmentForm: (state) => {
      state.currentStep = 0;
      state.mutationSuccess = false;
      state.submitSuccess = false;
      state.submitMessage = null;
      state.mutationError = null;
      state.shipmentData = initialState.shipmentData;
    },
    clearShipment: (state) => {
      state.currentStep = 0;
      state.mutationSuccess = false;
      state.submitSuccess = false;
      state.submitMessage = null;
      state.mutationError = null;
      state.mutationLoading = false;
      state.shipmentData = initialState.shipmentData;
    },
    clearMessages: (state) => {
      state.submitMessage = null;
      state.mutationError = null;
      state.mutationSuccess = null;
    },
    clearMutationState: (state) => {
      state.mutationLoading = false;
      state.mutationError = null;
      state.mutationSuccess = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(publishShipmentAPI.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(publishShipmentAPI.fulfilled, (state, action) => {
        state.mutationLoading = false;
        state.mutationSuccess = true;
        state.mutationError = null;
      })
      .addCase(publishShipmentAPI.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload || 'Something went wrong';
        state.mutationSuccess = false;
      })
      .addCase(submitShipmentAPI.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.submitSuccess = false;
      })
      .addCase(submitShipmentAPI.fulfilled, (state, action) => {
        state.mutationLoading = false;
        state.submitSuccess = true;
        state.submitMessage = action.payload?.message || 'Shipment submitted successfully';
        state.mutationError = null;
      })
      .addCase(submitShipmentAPI.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload || 'Failed to submit shipment';
        state.submitSuccess = false;
        state.submitMessage = null;
      })
      .addCase(GetCargoTypes.pending, (state) => {
        state.cargoTypesLoading = true;
        state.mutationError = null;
      })
      .addCase(GetCargoTypes.fulfilled, (state, action) => {
        state.cargoTypesLoading = false;
        state.cargoTypes = action.payload || [];
        state.mutationError = null;
      })
      .addCase(GetCargoTypes.rejected, (state, action) => {
        state.cargoTypesLoading = false;
        state.mutationError = action.payload || 'Failed to fetch cargo types';
        state.cargoTypes = [];
      });
  }
});

export const {
  setCurrentStep,
  nextStep,
  prevStep,
  updateShipmentData,
  updatePickupData,
  updateDeliveryData,
  updateCargoData,
  resetShipmentForm,
  clearShipment,
  clearCargoTypes,
  clearMessages,
  clearMutationState
} = shipmentSlice.actions;

export default shipmentSlice.reducer;